import { useState, useEffect } from 'react';
import { Save, RotateCw, ChevronDown, ChevronRight, Info, Check } from 'lucide-react';
import { useDarkMode } from '../../context/DarkModeContext';
import { api } from '../../lib/api';
import ConfirmDialog from './ConfirmDialog';

type Effort = 'low' | 'medium' | 'high' | 'xhigh' | 'max';

interface SectionPromptData {
  cardId: string;
  title: string;
  defaultPrompt: string;
  customPrompt: string | null;
  isCustom: boolean;
  model: 'sonnet' | 'opus';
  effort: Effort | null;
}

interface PromptsTabProps {
  setError: (msg: string) => void;
}

export default function PromptsTab({ setError }: PromptsTabProps) {
  const { darkMode } = useDarkMode();

  // === Prompts State ===
  const [masterPrompt, setMasterPrompt] = useState('');
  const [masterPromptOriginal, setMasterPromptOriginal] = useState('');
  const [masterIsCustom, setMasterIsCustom] = useState(false);
  const [masterSaving, setMasterSaving] = useState(false);
  const [masterSuccess, setMasterSuccess] = useState(false);
  const [sectionPrompts, setSectionPrompts] = useState<SectionPromptData[]>([]);
  const [promptsLoading, setPromptsLoading] = useState(false);
  const [expandedSection, setExpandedSection] = useState<string | null>(null);
  const [editingSectionText, setEditingSectionText] = useState<Record<string, string>>({});
  const [sectionSaving, setSectionSaving] = useState<string | null>(null);
  const [sectionSuccess, setSectionSuccess] = useState<string | null>(null);

  // === Website-Analyse-Prompt State ===
  const [websiteAnalysisPrompt, setWebsiteAnalysisPrompt] = useState('');
  const [websiteAnalysisPromptOriginal, setWebsiteAnalysisPromptOriginal] = useState('');
  const [websiteAnalysisIsCustom, setWebsiteAnalysisIsCustom] = useState(false);
  const [websiteAnalysisSaving, setWebsiteAnalysisSaving] = useState(false);
  const [websiteAnalysisSuccess, setWebsiteAnalysisSuccess] = useState(false);

  // === Analyse- & Überarbeitungs-Prompt State (Bericht-Optimierung) ===
  const [analysisPrompt, setAnalysisPrompt] = useState('');
  const [analysisPromptOriginal, setAnalysisPromptOriginal] = useState('');
  const [analysisIsCustom, setAnalysisIsCustom] = useState(false);
  const [analysisModel, setAnalysisModel] = useState<'sonnet' | 'opus'>('opus');
  const [analysisEffort, setAnalysisEffort] = useState<Effort | null>(null);
  const [analysisSaving, setAnalysisSaving] = useState(false);
  const [analysisSuccess, setAnalysisSuccess] = useState(false);
  const [reconstructPrompt, setReconstructPrompt] = useState('');
  const [reconstructPromptOriginal, setReconstructPromptOriginal] = useState('');
  const [reconstructIsCustom, setReconstructIsCustom] = useState(false);
  const [reconstructSaving, setReconstructSaving] = useState(false);
  const [reconstructSuccess, setReconstructSuccess] = useState(false);

  // === Reset-Confirm State ===
  const [resetConfirm, setResetConfirm] = useState<string | null>(null);

  useEffect(() => {
    void loadPrompts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // === Prompts Logic ===
  const loadPrompts = async () => {
    try {
      setPromptsLoading(true);
      const [masterData, sectionsData, waData, analysisData, reconstructData] = await Promise.all([
        api.get<{ text: string; isCustom: boolean }>('/api/prompts/admin/master'),
        api.get<SectionPromptData[]>('/api/prompts/admin/sections'),
        api.get<{ text: string; isCustom: boolean }>('/api/customers/admin/website-analysis-prompt'),
        api.get<{ text: string; isCustom: boolean; model: 'sonnet' | 'opus'; effort: Effort | null }>('/api/prompts/admin/analysis'),
        api.get<{ text: string; isCustom: boolean }>('/api/prompts/admin/reconstruct'),
      ]);
      setMasterPrompt(masterData.text);
      setMasterPromptOriginal(masterData.text);
      setMasterIsCustom(masterData.isCustom);
      setSectionPrompts(sectionsData);
      setWebsiteAnalysisPrompt(waData.text);
      setWebsiteAnalysisPromptOriginal(waData.text);
      setWebsiteAnalysisIsCustom(waData.isCustom);
      setAnalysisPrompt(analysisData.text);
      setAnalysisPromptOriginal(analysisData.text);
      setAnalysisIsCustom(analysisData.isCustom);
      setAnalysisModel(analysisData.model || 'opus');
      setAnalysisEffort(analysisData.effort ?? null);
      setReconstructPrompt(reconstructData.text);
      setReconstructPromptOriginal(reconstructData.text);
      setReconstructIsCustom(reconstructData.isCustom);
    } catch {
      setError('Fehler beim Laden der Prompts');
    } finally {
      setPromptsLoading(false);
    }
  };

  const handleSaveMasterPrompt = async () => {
    try {
      setMasterSaving(true);
      await api.post('/api/prompts/admin/master', { text: masterPrompt });
      setMasterPromptOriginal(masterPrompt);
      setMasterIsCustom(true);
      setMasterSuccess(true);
      setTimeout(() => setMasterSuccess(false), 2000);
    } catch {
      setError('Fehler beim Speichern des Master-Prompts');
    } finally {
      setMasterSaving(false);
    }
  };

  const handleResetMasterPrompt = async () => {
    try {
      setMasterSaving(true);
      await api.delete('/api/prompts/admin/master');
      const defaultData = await api.get<{ text: string }>('/api/prompts/admin/master/default');
      setMasterPrompt(defaultData.text);
      setMasterPromptOriginal(defaultData.text);
      setMasterIsCustom(false);
      setMasterSuccess(true);
      setTimeout(() => setMasterSuccess(false), 2000);
    } catch {
      setError('Fehler beim Zurücksetzen des Master-Prompts');
    } finally {
      setMasterSaving(false);
    }
  };

  // === Website-Analyse-Prompt Logic ===
  const handleSaveWebsiteAnalysisPrompt = async () => {
    try {
      setWebsiteAnalysisSaving(true);
      await api.post('/api/customers/admin/website-analysis-prompt', { text: websiteAnalysisPrompt });
      setWebsiteAnalysisPromptOriginal(websiteAnalysisPrompt);
      setWebsiteAnalysisIsCustom(true);
      setWebsiteAnalysisSuccess(true);
      setTimeout(() => setWebsiteAnalysisSuccess(false), 2000);
    } catch {
      setError('Fehler beim Speichern des Website-Analyse-Prompts');
    } finally {
      setWebsiteAnalysisSaving(false);
    }
  };

  const handleResetWebsiteAnalysisPrompt = async () => {
    try {
      setWebsiteAnalysisSaving(true);
      await api.delete('/api/customers/admin/website-analysis-prompt');
      const defaultData = await api.get<{ text: string }>('/api/customers/admin/website-analysis-prompt/default');
      setWebsiteAnalysisPrompt(defaultData.text);
      setWebsiteAnalysisPromptOriginal(defaultData.text);
      setWebsiteAnalysisIsCustom(false);
      setWebsiteAnalysisSuccess(true);
      setTimeout(() => setWebsiteAnalysisSuccess(false), 2000);
    } catch {
      setError('Fehler beim Zurücksetzen des Website-Analyse-Prompts');
    } finally {
      setWebsiteAnalysisSaving(false);
    }
  };

  // === Analyse-Prompt Logic (Bericht-Optimierung) ===
  const handleSaveAnalysisPrompt = async () => {
    try {
      setAnalysisSaving(true);
      await api.post('/api/prompts/admin/analysis', { text: analysisPrompt, model: analysisModel, effort: analysisEffort ?? undefined });
      setAnalysisPromptOriginal(analysisPrompt);
      setAnalysisIsCustom(true);
      setAnalysisSuccess(true);
      setTimeout(() => setAnalysisSuccess(false), 2000);
    } catch {
      setError('Fehler beim Speichern des Analyse-Prompts');
    } finally {
      setAnalysisSaving(false);
    }
  };

  const handleChangeAnalysisModel = async (m: 'sonnet' | 'opus') => {
    setAnalysisModel(m);
    try {
      setAnalysisSaving(true);
      await api.post('/api/prompts/admin/analysis', { text: analysisPrompt, model: m, effort: analysisEffort ?? undefined });
      setAnalysisIsCustom(true);
    } catch {
      setError('Fehler beim Speichern des Modells');
    } finally {
      setAnalysisSaving(false);
    }
  };

  const handleChangeAnalysisEffort = async (raw: string) => {
    const eff: Effort | null = raw === '' ? null : (raw as Effort);
    setAnalysisEffort(eff);
    try {
      setAnalysisSaving(true);
      await api.post('/api/prompts/admin/analysis', { text: analysisPrompt, model: analysisModel, effort: eff ?? undefined });
      setAnalysisIsCustom(true);
    } catch {
      setError('Fehler beim Speichern des Effort-Werts');
    } finally {
      setAnalysisSaving(false);
    }
  };

  const handleResetAnalysisPrompt = async () => {
    try {
      setAnalysisSaving(true);
      await api.delete('/api/prompts/admin/analysis');
      const def = await api.get<{ text: string }>('/api/prompts/admin/analysis/default');
      setAnalysisPrompt(def.text);
      setAnalysisPromptOriginal(def.text);
      setAnalysisIsCustom(false);
      setAnalysisModel('opus');
      setAnalysisEffort(null);
      setAnalysisSuccess(true);
      setTimeout(() => setAnalysisSuccess(false), 2000);
    } catch {
      setError('Fehler beim Zurücksetzen des Analyse-Prompts');
    } finally {
      setAnalysisSaving(false);
    }
  };

  // === Überarbeitungs-Prompt Logic (Bericht-Optimierung) ===
  const handleSaveReconstructPrompt = async () => {
    try {
      setReconstructSaving(true);
      await api.post('/api/prompts/admin/reconstruct', { text: reconstructPrompt });
      setReconstructPromptOriginal(reconstructPrompt);
      setReconstructIsCustom(true);
      setReconstructSuccess(true);
      setTimeout(() => setReconstructSuccess(false), 2000);
    } catch {
      setError('Fehler beim Speichern des Überarbeitungs-Prompts');
    } finally {
      setReconstructSaving(false);
    }
  };

  const handleResetReconstructPrompt = async () => {
    try {
      setReconstructSaving(true);
      await api.delete('/api/prompts/admin/reconstruct');
      const def = await api.get<{ text: string }>('/api/prompts/admin/reconstruct/default');
      setReconstructPrompt(def.text);
      setReconstructPromptOriginal(def.text);
      setReconstructIsCustom(false);
      setReconstructSuccess(true);
      setTimeout(() => setReconstructSuccess(false), 2000);
    } catch {
      setError('Fehler beim Zurücksetzen des Überarbeitungs-Prompts');
    } finally {
      setReconstructSaving(false);
    }
  };

  const handleSaveSectionPrompt = async (cardId: string) => {
    const text = editingSectionText[cardId];
    if (!text) return;
    const section = sectionPrompts.find(s => s.cardId === cardId);
    try {
      setSectionSaving(cardId);
      await api.post(`/api/prompts/admin/sections/${cardId}`, {
        text,
        model: section?.model || 'sonnet',
        effort: section?.effort ?? undefined,
      });
      setSectionPrompts(prev => prev.map(s =>
        s.cardId === cardId ? { ...s, customPrompt: text, isCustom: true } : s
      ));
      setSectionSuccess(cardId);
      setTimeout(() => setSectionSuccess(null), 2000);
    } catch {
      setError('Fehler beim Speichern des Section-Prompts');
    } finally {
      setSectionSaving(null);
    }
  };

  const handleChangeSectionModel = async (cardId: string, newModel: 'sonnet' | 'opus') => {
    const section = sectionPrompts.find(s => s.cardId === cardId);
    if (!section) return;
    // Optimistisch updaten, dann speichern.
    setSectionPrompts(prev => prev.map(s =>
      s.cardId === cardId ? { ...s, model: newModel } : s
    ));
    try {
      setSectionSaving(cardId);
      // Wir senden den aktuell gespeicherten Text mit, damit der Backend-DTO
      // nicht den Default-Prompt ueberschreibt. Wenn kein custom Prompt vorliegt,
      // bleibt isCustom=true und der bisher gezeigte Text wird fixiert — das ist
      // OK, weil sich am Prompt-Text nichts aendert.
      const text = editingSectionText[cardId] ?? section.customPrompt ?? section.defaultPrompt;
      await api.post(`/api/prompts/admin/sections/${cardId}`, {
        text,
        model: newModel,
        effort: section.effort ?? undefined,
      });
      setSectionPrompts(prev => prev.map(s =>
        s.cardId === cardId ? { ...s, customPrompt: text, isCustom: true } : s
      ));
      setSectionSuccess(cardId);
      setTimeout(() => setSectionSuccess(null), 2000);
    } catch {
      setError('Fehler beim Speichern des Modells');
      // Rollback bei Fehler
      setSectionPrompts(prev => prev.map(s =>
        s.cardId === cardId ? { ...s, model: section.model } : s
      ));
    } finally {
      setSectionSaving(null);
    }
  };

  /**
   * Setzt Effort fuer eine Card (#3). Leerwert ('') → null (SDK-Default, entspricht 'high').
   * Haiku unterstuetzt effort nicht; wir haben im Moment aber nur sonnet/opus.
   */
  const handleChangeSectionEffort = async (cardId: string, rawValue: string) => {
    const section = sectionPrompts.find(s => s.cardId === cardId);
    if (!section) return;
    const newEffort: Effort | null = rawValue === '' ? null : (rawValue as Effort);
    const previousEffort = section.effort;
    setSectionPrompts(prev => prev.map(s =>
      s.cardId === cardId ? { ...s, effort: newEffort } : s
    ));
    try {
      setSectionSaving(cardId);
      const text = editingSectionText[cardId] ?? section.customPrompt ?? section.defaultPrompt;
      await api.post(`/api/prompts/admin/sections/${cardId}`, {
        text,
        model: section.model,
        effort: newEffort ?? undefined,
      });
      setSectionPrompts(prev => prev.map(s =>
        s.cardId === cardId ? { ...s, customPrompt: text, isCustom: true } : s
      ));
      setSectionSuccess(cardId);
      setTimeout(() => setSectionSuccess(null), 2000);
    } catch {
      setError('Fehler beim Speichern des Effort-Werts');
      setSectionPrompts(prev => prev.map(s =>
        s.cardId === cardId ? { ...s, effort: previousEffort } : s
      ));
    } finally {
      setSectionSaving(null);
    }
  };

  const handleResetSectionPrompt = (cardId: string) => {
    setResetConfirm(cardId);
  };

  const performResetSectionPrompt = async (cardId: string) => {
    setResetConfirm(null);
    try {
      setSectionSaving(cardId);
      await api.delete(`/api/prompts/admin/sections/${cardId}`);
      setSectionPrompts(prev => prev.map(s =>
        s.cardId === cardId ? { ...s, customPrompt: null, isCustom: false } : s
      ));
      setEditingSectionText(prev => {
        const next = { ...prev };
        delete next[cardId];
        return next;
      });
      setSectionSuccess(cardId);
      setTimeout(() => setSectionSuccess(null), 2000);
    } catch {
      setError('Fehler beim Zurücksetzen des Section-Prompts');
    } finally {
      setSectionSaving(null);
    }
  };

  const toggleSection = (cardId: string) => {
    if (expandedSection === cardId) {
      setExpandedSection(null);
    } else {
      setExpandedSection(cardId);
      // Lade den aktuellen Text in den Editor
      const section = sectionPrompts.find(s => s.cardId === cardId);
      if (section && !editingSectionText[cardId]) {
        setEditingSectionText(prev => ({
          ...prev,
          [cardId]: section.customPrompt || section.defaultPrompt,
        }));
      }
    }
  };

  const card = darkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200';

  return (
    <>
      {promptsLoading ? (
        <div className={`rounded-xl border p-8 text-center ${card}`}>
          <p className="text-slate-400">Lädt Prompts...</p>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Master-Prompt */}
          <div className={`rounded-xl border ${card}`}>
            <div className="p-4 border-b flex items-center justify-between" style={{ borderColor: darkMode ? '#334155' : '#e2e8f0' }}>
              <div>
                <h3 className={`text-base font-semibold ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                  Master-Prompt (System-Level)
                </h3>
                <p className={`text-xs mt-0.5 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                  Wird bei jeder Textgenerierung unsichtbar vorgeschaltet. Definiert Rolle, Stil und Formatierung.
                  {masterIsCustom && (
                    <span className="ml-2 inline-flex items-center gap-1 text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded font-medium">
                      Angepasst
                    </span>
                  )}
                </p>
              </div>
              <div className="flex items-center gap-2">
                {masterIsCustom && (
                  <button
                    onClick={handleResetMasterPrompt}
                    disabled={masterSaving}
                    className={`flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg border transition-colors ${
                      darkMode ? 'border-slate-600 text-slate-300 hover:bg-slate-700' : 'border-slate-300 text-slate-600 hover:bg-slate-50'
                    }`}
                  >
                    <RotateCw className="w-3.5 h-3.5" />
                    Auf Default zurücksetzen
                  </button>
                )}
                <button
                  onClick={handleSaveMasterPrompt}
                  disabled={masterSaving || masterPrompt === masterPromptOriginal}
                  className={`flex items-center gap-1.5 px-4 py-1.5 text-xs rounded-lg font-medium transition-colors ${
                    masterSuccess
                      ? 'bg-green-600 text-white'
                      : masterPrompt !== masterPromptOriginal
                        ? 'bg-blue-600 text-white hover:bg-blue-700'
                        : darkMode ? 'bg-slate-700 text-slate-500' : 'bg-slate-100 text-slate-400'
                  }`}
                >
                  {masterSuccess ? (
                    <><Check className="w-3.5 h-3.5" /> Gespeichert</>
                  ) : masterSaving ? (
                    'Speichert...'
                  ) : (
                    <><Save className="w-3.5 h-3.5" /> Speichern</>
                  )}
                </button>
              </div>
            </div>
            <div className="p-4">
              <textarea
                value={masterPrompt}
                onChange={e => setMasterPrompt(e.target.value)}
                rows={16}
                className={`w-full px-4 py-3 border rounded-lg text-sm font-mono leading-relaxed focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-y ${
                  darkMode ? 'bg-slate-900 border-slate-600 text-slate-200 placeholder-slate-500' : 'bg-slate-50 border-slate-300 text-slate-800'
                }`}
              />
              <p className={`mt-2 text-xs ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                {masterPrompt.length} Zeichen · Änderungen gelten für alle zukünftigen Textgenerierungen
              </p>
            </div>
          </div>

          {/* Section-Prompts */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <div>
                <h3 className={`text-base font-semibold ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                  Abschnitts-Prompts
                </h3>
                <p className={`text-xs mt-0.5 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                  Prompts für jeden Berichtsabschnitt. Werden im Editor-Popup angezeigt und sind dort editierbar.
                </p>
              </div>
            </div>

            <div className="space-y-2">
              {sectionPrompts.map(section => (
                <div key={section.cardId} className={`rounded-xl border overflow-hidden transition-all ${card}`}>
                  {/* Section Header (klickbar bis auf Modell-Dropdown) */}
                  <div
                    className={`w-full flex items-center justify-between px-4 py-3 text-left transition-colors ${
                      darkMode ? 'hover:bg-slate-700/50' : 'hover:bg-slate-50'
                    }`}
                  >
                    <button
                      onClick={() => toggleSection(section.cardId)}
                      className="flex items-center gap-3 flex-1 min-w-0 text-left"
                    >
                      {expandedSection === section.cardId
                        ? <ChevronDown className="w-4 h-4 text-blue-500" />
                        : <ChevronRight className={`w-4 h-4 ${darkMode ? 'text-slate-500' : 'text-slate-400'}`} />
                      }
                      <span className={`text-sm font-medium truncate ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                        {section.title}
                      </span>
                      {section.isCustom && (
                        <span className="text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded font-medium">
                          Angepasst
                        </span>
                      )}
                    </button>
                    <div className="flex items-center gap-3 shrink-0">
                      {/* Modell-Dropdown (nicht fuer Direct-Render Cards) */}
                      {!['card-1-2', 'card-1-3', 'card-1-5'].includes(section.cardId) && (
                        <>
                          <select
                            value={section.model}
                            onChange={e => handleChangeSectionModel(section.cardId, e.target.value as 'sonnet' | 'opus')}
                            onClick={e => e.stopPropagation()}
                            disabled={sectionSaving === section.cardId}
                            className={`text-xs px-2 py-1 rounded border font-medium ${
                              section.model === 'opus'
                                ? darkMode ? 'bg-purple-900/40 border-purple-700 text-purple-300' : 'bg-purple-50 border-purple-300 text-purple-700'
                                : darkMode ? 'bg-slate-700 border-slate-600 text-slate-200' : 'bg-slate-50 border-slate-300 text-slate-700'
                            } disabled:opacity-50`}
                            title="Claude-Modell fuer diese Card"
                          >
                            <option value="sonnet">Sonnet</option>
                            <option value="opus">Opus</option>
                          </select>
                          {/* Effort-Dropdown (#3) — SDK-only; wirkt nur wenn ANTHROPIC_API_KEY gesetzt ist */}
                          <select
                            value={section.effort ?? ''}
                            onChange={e => handleChangeSectionEffort(section.cardId, e.target.value)}
                            onClick={e => e.stopPropagation()}
                            disabled={sectionSaving === section.cardId}
                            className={`text-xs px-2 py-1 rounded border font-medium ${
                              darkMode ? 'bg-slate-700 border-slate-600 text-slate-200' : 'bg-slate-50 border-slate-300 text-slate-700'
                            } disabled:opacity-50`}
                            title="Effort: SDK output_config.effort — steuert Denk-Tiefe und Kosten. Leer = Default (high). XHigh nur auf Opus 4.7."
                          >
                            <option value="">Effort: Default</option>
                            <option value="low">Low</option>
                            <option value="medium">Medium</option>
                            <option value="high">High</option>
                            {section.model === 'opus' && (
                              <option value="xhigh">XHigh (Opus 4.7)</option>
                            )}
                            {section.model === 'opus' && (
                              <option value="max">Max (Opus)</option>
                            )}
                          </select>
                        </>
                      )}
                      <span className={`text-xs ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                        {section.cardId}
                      </span>
                    </div>
                  </div>

                  {/* Expanded Content */}
                  {expandedSection === section.cardId && (
                    <div className={`border-t px-4 py-4 ${darkMode ? 'border-slate-700' : 'border-slate-200'}`}>
                      {['card-1-2', 'card-1-3', 'card-1-5'].includes(section.cardId) ? (
                        /* Direct-Render Hinweis */
                        <div className={`flex items-start gap-3 p-4 rounded-lg border ${
                          darkMode ? 'bg-blue-900/20 border-blue-800 text-blue-300' : 'bg-blue-50 border-blue-200 text-blue-700'
                        }`}>
                          <Info className="w-5 h-5 mt-0.5 flex-shrink-0" />
                          <div>
                            <p className="text-sm font-medium mb-1">Direktes Rendering</p>
                            <p className={`text-xs ${darkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                              Dieser Abschnitt wird direkt aus den Formulardaten als HTML gerendert – ohne KI-Generierung.
                              Der Text wird automatisch aus den eingegebenen Daten zusammengebaut.
                            </p>
                          </div>
                        </div>
                      ) : (
                        /* Normaler Prompt-Editor */
                        <>
                      <textarea
                        value={editingSectionText[section.cardId] ?? (section.customPrompt || section.defaultPrompt)}
                        onChange={e => setEditingSectionText(prev => ({ ...prev, [section.cardId]: e.target.value }))}
                        rows={10}
                        className={`w-full px-4 py-3 border rounded-lg text-sm font-mono leading-relaxed focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-y ${
                          darkMode ? 'bg-slate-900 border-slate-600 text-slate-200' : 'bg-slate-50 border-slate-300 text-slate-800'
                        }`}
                      />
                      <div className="flex items-center justify-between mt-3">
                        <p className={`text-xs ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                          {(editingSectionText[section.cardId] ?? (section.customPrompt || section.defaultPrompt)).length} Zeichen
                        </p>
                        <div className="flex items-center gap-2">
                          {section.isCustom && (
                            <button
                              onClick={() => handleResetSectionPrompt(section.cardId)}
                              disabled={sectionSaving === section.cardId}
                              className={`flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg border transition-colors ${
                                darkMode ? 'border-slate-600 text-slate-300 hover:bg-slate-700' : 'border-slate-300 text-slate-600 hover:bg-slate-50'
                              }`}
                            >
                              <RotateCw className="w-3.5 h-3.5" />
                              Zurücksetzen
                            </button>
                          )}
                          <button
                            onClick={() => handleSaveSectionPrompt(section.cardId)}
                            disabled={sectionSaving === section.cardId || !editingSectionText[section.cardId]}
                            className={`flex items-center gap-1.5 px-4 py-1.5 text-xs rounded-lg font-medium transition-colors ${
                              sectionSuccess === section.cardId
                                ? 'bg-green-600 text-white'
                                : editingSectionText[section.cardId]
                                  ? 'bg-blue-600 text-white hover:bg-blue-700'
                                  : darkMode ? 'bg-slate-700 text-slate-500' : 'bg-slate-100 text-slate-400'
                            }`}
                          >
                            {sectionSuccess === section.cardId ? (
                              <><Check className="w-3.5 h-3.5" /> Gespeichert</>
                            ) : sectionSaving === section.cardId ? (
                              'Speichert...'
                            ) : (
                              <><Save className="w-3.5 h-3.5" /> Speichern</>
                            )}
                          </button>
                        </div>
                      </div>
                        </>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          <p className={`text-xs ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
            {sectionPrompts.length} Abschnitte · {sectionPrompts.filter(s => s.isCustom).length} angepasst · Änderungen gelten für alle zukünftigen Generierungen
          </p>

          {/* Website-Analyse-Prompt */}
          <div className={`rounded-xl border overflow-hidden ${darkMode ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'}`}>
            <div className="flex items-center justify-between px-5 py-4">
              <div>
                <h3 className={`text-base font-semibold ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                  Website-Analyse-Prompt
                </h3>
                <p className={`text-xs mt-0.5 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                  Wird bei der automatischen Website-Analyse neuer Kunden verwendet. Ergebnis fließt als Kontext in die Textgenerierung ein.
                  {websiteAnalysisIsCustom && (
                    <span className="ml-2 inline-flex items-center gap-1 text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded font-medium">
                      Angepasst
                    </span>
                  )}
                </p>
              </div>
              <div className="flex items-center gap-2">
                {websiteAnalysisIsCustom && (
                  <button
                    onClick={handleResetWebsiteAnalysisPrompt}
                    disabled={websiteAnalysisSaving}
                    className={`flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg border transition-colors ${
                      darkMode ? 'border-slate-600 text-slate-300 hover:bg-slate-700' : 'border-slate-300 text-slate-600 hover:bg-slate-50'
                    }`}
                  >
                    <RotateCw className="w-3.5 h-3.5" />
                    Auf Default zurücksetzen
                  </button>
                )}
                <button
                  onClick={handleSaveWebsiteAnalysisPrompt}
                  disabled={websiteAnalysisSaving || websiteAnalysisPrompt === websiteAnalysisPromptOriginal}
                  className={`flex items-center gap-1.5 px-4 py-1.5 text-xs rounded-lg font-medium transition-colors ${
                    websiteAnalysisSuccess
                      ? 'bg-green-600 text-white'
                      : websiteAnalysisPrompt !== websiteAnalysisPromptOriginal
                        ? 'bg-blue-600 text-white hover:bg-blue-700'
                        : darkMode ? 'bg-slate-700 text-slate-500' : 'bg-slate-100 text-slate-400'
                  }`}
                >
                  {websiteAnalysisSuccess ? (
                    <><Check className="w-3.5 h-3.5" /> Gespeichert</>
                  ) : websiteAnalysisSaving ? (
                    'Speichert...'
                  ) : (
                    <><Save className="w-3.5 h-3.5" /> Speichern</>
                  )}
                </button>
              </div>
            </div>
            <div className="px-5 pb-4">
              <textarea
                value={websiteAnalysisPrompt}
                onChange={e => setWebsiteAnalysisPrompt(e.target.value)}
                rows={14}
                className={`w-full px-4 py-3 border rounded-lg text-sm font-mono leading-relaxed focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-y ${
                  darkMode ? 'bg-slate-900 border-slate-600 text-slate-200 placeholder-slate-500' : 'bg-slate-50 border-slate-300 text-slate-800'
                }`}
              />
              <p className={`mt-2 text-xs ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                {websiteAnalysisPrompt.length} Zeichen · Änderungen gelten für alle zukünftigen Website-Analysen
              </p>
            </div>
          </div>

          {/* Analyse-Prompt (Bericht-Optimierung) */}
          <div className={`rounded-xl border overflow-hidden ${darkMode ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'}`}>
            <div className="flex items-center justify-between px-5 py-4 flex-wrap gap-2">
              <div>
                <h3 className={`text-base font-semibold ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                  Analyse-Prompt (Bericht-Optimierung)
                </h3>
                <p className={`text-xs mt-0.5 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                  Steuert die KI-Bewertung (Förderfähigkeit & Qualität) im Editor. Muss reines JSON liefern — bewusst OHNE Master-/HTML-Kontext.
                  {analysisIsCustom && (
                    <span className="ml-2 inline-flex items-center gap-1 text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded font-medium">Angepasst</span>
                  )}
                </p>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <select
                  value={analysisModel}
                  onChange={e => handleChangeAnalysisModel(e.target.value as 'sonnet' | 'opus')}
                  disabled={analysisSaving}
                  className={`text-xs rounded-lg border px-2 py-1.5 ${darkMode ? 'bg-slate-900 border-slate-600 text-slate-200' : 'bg-white border-slate-300 text-slate-700'}`}
                >
                  <option value="opus">Opus</option>
                  <option value="sonnet">Sonnet</option>
                </select>
                <select
                  value={analysisEffort ?? ''}
                  onChange={e => handleChangeAnalysisEffort(e.target.value)}
                  disabled={analysisSaving}
                  className={`text-xs rounded-lg border px-2 py-1.5 ${darkMode ? 'bg-slate-900 border-slate-600 text-slate-200' : 'bg-white border-slate-300 text-slate-700'}`}
                >
                  <option value="">Effort: Standard</option>
                  <option value="low">low</option>
                  <option value="medium">medium</option>
                  <option value="high">high</option>
                  <option value="xhigh">xhigh (Opus)</option>
                  <option value="max">max</option>
                </select>
                {analysisIsCustom && (
                  <button
                    onClick={handleResetAnalysisPrompt}
                    disabled={analysisSaving}
                    className={`flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg border transition-colors ${darkMode ? 'border-slate-600 text-slate-300 hover:bg-slate-700' : 'border-slate-300 text-slate-600 hover:bg-slate-50'}`}
                  >
                    <RotateCw className="w-3.5 h-3.5" /> Default
                  </button>
                )}
                <button
                  onClick={handleSaveAnalysisPrompt}
                  disabled={analysisSaving || analysisPrompt === analysisPromptOriginal}
                  className={`flex items-center gap-1.5 px-4 py-1.5 text-xs rounded-lg font-medium transition-colors ${
                    analysisSuccess ? 'bg-green-600 text-white'
                      : analysisPrompt !== analysisPromptOriginal ? 'bg-blue-600 text-white hover:bg-blue-700'
                      : darkMode ? 'bg-slate-700 text-slate-500' : 'bg-slate-100 text-slate-400'
                  }`}
                >
                  {analysisSuccess ? (<><Check className="w-3.5 h-3.5" /> Gespeichert</>) : analysisSaving ? ('Speichert...') : (<><Save className="w-3.5 h-3.5" /> Speichern</>)}
                </button>
              </div>
            </div>
            <div className="px-5 pb-4">
              <textarea
                value={analysisPrompt}
                onChange={e => setAnalysisPrompt(e.target.value)}
                rows={14}
                className={`w-full px-4 py-3 border rounded-lg text-sm font-mono leading-relaxed focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-y ${darkMode ? 'bg-slate-900 border-slate-600 text-slate-200 placeholder-slate-500' : 'bg-slate-50 border-slate-300 text-slate-800'}`}
              />
              <p className={`mt-2 text-xs ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                {analysisPrompt.length} Zeichen · Modell/Effort werden sofort gespeichert
              </p>
            </div>
          </div>

          {/* Überarbeitungs-Prompt (Bericht-Optimierung) */}
          <div className={`rounded-xl border overflow-hidden ${darkMode ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'}`}>
            <div className="flex items-center justify-between px-5 py-4 flex-wrap gap-2">
              <div>
                <h3 className={`text-base font-semibold ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                  Überarbeitungs-Prompt (Bericht-Optimierung)
                </h3>
                <p className={`text-xs mt-0.5 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                  Steuert die gezielte Block-Überarbeitung (HTML-Ausgabe; Master-Regeln gelten). Das Modell richtet sich nach der jeweiligen Karte.
                  {reconstructIsCustom && (
                    <span className="ml-2 inline-flex items-center gap-1 text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded font-medium">Angepasst</span>
                  )}
                </p>
              </div>
              <div className="flex items-center gap-2">
                {reconstructIsCustom && (
                  <button
                    onClick={handleResetReconstructPrompt}
                    disabled={reconstructSaving}
                    className={`flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg border transition-colors ${darkMode ? 'border-slate-600 text-slate-300 hover:bg-slate-700' : 'border-slate-300 text-slate-600 hover:bg-slate-50'}`}
                  >
                    <RotateCw className="w-3.5 h-3.5" /> Default
                  </button>
                )}
                <button
                  onClick={handleSaveReconstructPrompt}
                  disabled={reconstructSaving || reconstructPrompt === reconstructPromptOriginal}
                  className={`flex items-center gap-1.5 px-4 py-1.5 text-xs rounded-lg font-medium transition-colors ${
                    reconstructSuccess ? 'bg-green-600 text-white'
                      : reconstructPrompt !== reconstructPromptOriginal ? 'bg-blue-600 text-white hover:bg-blue-700'
                      : darkMode ? 'bg-slate-700 text-slate-500' : 'bg-slate-100 text-slate-400'
                  }`}
                >
                  {reconstructSuccess ? (<><Check className="w-3.5 h-3.5" /> Gespeichert</>) : reconstructSaving ? ('Speichert...') : (<><Save className="w-3.5 h-3.5" /> Speichern</>)}
                </button>
              </div>
            </div>
            <div className="px-5 pb-4">
              <textarea
                value={reconstructPrompt}
                onChange={e => setReconstructPrompt(e.target.value)}
                rows={10}
                className={`w-full px-4 py-3 border rounded-lg text-sm font-mono leading-relaxed focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-y ${darkMode ? 'bg-slate-900 border-slate-600 text-slate-200 placeholder-slate-500' : 'bg-slate-50 border-slate-300 text-slate-800'}`}
              />
              <p className={`mt-2 text-xs ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                {reconstructPrompt.length} Zeichen
              </p>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={resetConfirm !== null}
        title="Prompt zurücksetzen"
        message="Möchtest du den Prompt auf den Standard zurücksetzen? Deine Änderungen gehen verloren."
        confirmLabel="Zurücksetzen"
        onConfirm={() => resetConfirm && performResetSectionPrompt(resetConfirm)}
        onCancel={() => setResetConfirm(null)}
      />
    </>
  );
}
